import { DocumentRow } from '@/types/documents'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<DocumentRow>({
  pathCategory: 'document_rows',
})

export function useGetDocumentRow({
  id,
  ...options
}: UseGetDetailOptions<DocumentRow>) {
  const query = useGetDetail<DocumentRow>({
    id,
    queryKey: queryKeys.documentRows.detail({ id, filters: options.filters }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    documentRow: query.data,
    ...query,
  }
}

export function useCreateDocumentRow(
  options: UseCreateDetailOptions<DocumentRow> = {},
) {
  const mutation = useCreateDetail<DocumentRow>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDocumentRow: mutation.mutate,
    createDocumentRowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateDocumentRow(
  options: UseUpdateDetailOptions<DocumentRow> = {},
) {
  const mutation = useUpdateDetail<DocumentRow>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateDocumentRow: mutation.mutate,
    updateDocumentRowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocumentRow(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<DocumentRow>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteDocumentRow: mutation.mutate,
    deleteDocumentRowAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetDocumentRows({
  filters,
  ...options
}: UseGetListOptions<DocumentRow> = {}) {
  const query = useGetList<DocumentRow>({
    filters,
    queryKey: queryKeys.documentRows.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    documentRows: query.allData,
    ...query,
  }
}

export function useUpdateDocumentRows(
  options: UseUpdateListOptions<DocumentRow> = {},
) {
  const mutation = useUpdateList<DocumentRow>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateDocumentRows: mutation.mutate,
    updateDocumentRowsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocumentRows({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<DocumentRow>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteDocumentRows: mutation.mutate,
    deleteDocumentRowsAsync: mutation.mutateAsync,
    ...mutation,
  }
}
